import { useState, useEffect } from "react";
import { Link } from "wouter";
import { useAuth } from "@/context/auth";
import { useBranch } from "@/context/branch";
import { formatCurrency } from "@/lib/currency";
import { readJsonArrayOrEmpty } from "@/lib/http";
import { sortSalesNewestFirst } from "@/lib/sales-order";
import { DashboardMoneyToggle, useDashboardMoneyVisibility } from "./dashboard-money-visibility";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp, BarChart3, Package, Layers,
  ArrowRight, AlertTriangle, Banknote, Receipt, Users, ShoppingCart,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function StatCard({ label, value, sub, icon: Icon, color }: {
  label: string; value: string; sub?: string; icon: React.ElementType; color: string;
}) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-start justify-between mb-3">
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function FabricTextileDashboard() {
  const { user, store } = useAuth();
  const { activeBranchId, activeBranch } = useBranch();
  const branchQuery = activeBranchId ? `?branchId=${activeBranchId}` : "";
  const isOwner = user?.role === "admin" || user?.role === "superadmin";
  const isManager = user?.role === "manager" || isOwner;
  const { moneyVisible, toggleMoneyVisibility, maskMoney } = useDashboardMoneyVisibility();

  const [stats, setStats] = useState({
    todayRevenue: 0, todaySales: 0,
    todayCash: 0, todayTransfer: 0,
    totalLines: 0, lowStockRolls: 0, outOfStock: 0,
    monthRevenue: 0,
  });
  const [fabricStock, setFabricStock] = useState<any[]>([]);
  const [lowStock, setLowStock] = useState<any[]>([]);
  const [todaySales, setTodaySales] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const lagosDate = (value: Date | string) => new Date(value).toLocaleDateString("en-CA", { timeZone: "Africa/Lagos" });
        const today = lagosDate(new Date());
        const currentMonth = today.slice(0, 7);
        const [salesRes, productsRes] = await Promise.allSettled([
          fetch(`${BASE}/api/sales${branchQuery}`, { credentials: "include", cache: "no-store" }),
          fetch(`${BASE}/api/products${branchQuery}`, { credentials: "include" }),
        ]);
        const sales = salesRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(salesRes.value) : [];
        const products = productsRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(productsRes.value) : [];

        const newestSales = sortSalesNewestFirst(sales);
        const todayList = newestSales.filter((s: any) => lagosDate(s.createdAt) === today);
        const monthList = newestSales.filter((s: any) => lagosDate(s.createdAt).startsWith(currentMonth));

        const activeItems = products as any[];
        const lowItems = activeItems.filter((p: any) => p.stock > 0 && p.stock <= (p.reorderLevel ?? 5));
        const outItems = activeItems.filter((p: any) => p.stock === 0);

        const todayRev = todayList.reduce((s: number, x: any) => s + Number(x.totalAmount ?? x.total ?? 0), 0);
        const todayCash = todayList
          .filter((s: any) => (s.paymentMethod || "cash").toLowerCase() === "cash")
          .reduce((a: number, s: any) => a + Number(s.totalAmount ?? s.total ?? 0), 0);

        setStats({
          todayRevenue: todayRev,
          todaySales: todayList.length,
          todayCash,
          todayTransfer: todayRev - todayCash,
          totalLines: activeItems.length,
          lowStockRolls: lowItems.length,
          outOfStock: outItems.length,
          monthRevenue: monthList.reduce((s: number, x: any) => s + Number(x.totalAmount ?? x.total ?? 0), 0),
        });

        // Sort: out of stock first, then low, then by stock ascending
        setFabricStock(
          activeItems
            .sort((a: any, b: any) => a.stock - b.stock)
            .slice(0, 8)
        );
        setLowStock([...outItems, ...lowItems].slice(0, 4));
        setTodaySales(todayList.slice(0, 6));
      } finally { setLoading(false); }
    };
    load();
  }, [branchQuery]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl">🪢</span>
            <h1 className="text-2xl font-bold">{store?.name || "My Fabric Store"}</h1>
            <Badge className="bg-purple-100 text-purple-700 border-purple-200">Fabric & Textile</Badge>
            <Badge variant="outline" className="capitalize">{user?.role}</Badge>
          </div>
          <p className="text-muted-foreground text-sm">
            Fabric & Textile Merchant Dashboard · {activeBranch?.name ?? "All Branches"} · {new Date().toLocaleDateString("en-NG", { weekday: "long", day: "numeric", month: "long" })}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <DashboardMoneyToggle visible={moneyVisible} onToggle={toggleMoneyVisibility} />
          <Link href="/customers">
            <Button variant="outline" size="sm" className="gap-1.5"><Users className="w-3.5 h-3.5" />Customers</Button>
          </Link>
          <Link href="/sales/new">
            <Button size="sm" className="gap-1.5 bg-purple-600 hover:bg-purple-700"><ShoppingCart className="w-3.5 h-3.5" />New Sale</Button>
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Today's Revenue"
          value={maskMoney(formatCurrency(stats.todayRevenue))}
          icon={TrendingUp}
          color="bg-purple-100 text-purple-600"
          sub={`${stats.todaySales} sale${stats.todaySales !== 1 ? "s" : ""} today`}
        />
        <StatCard
          label="This Month"
          value={maskMoney(formatCurrency(stats.monthRevenue))}
          icon={BarChart3}
          color="bg-indigo-100 text-indigo-600"
          sub="monthly earnings"
        />
        <StatCard
          label="Fabric Lines"
          value={String(stats.totalLines)}
          icon={stats.outOfStock > 0 ? AlertTriangle : Layers}
          color={stats.outOfStock > 0 ? "bg-red-100 text-red-600" : stats.lowStockRolls > 0 ? "bg-amber-100 text-amber-600" : "bg-emerald-100 text-emerald-600"}
          sub={
            stats.outOfStock > 0
              ? `${stats.outOfStock} fabric${stats.outOfStock !== 1 ? "s" : ""} out of stock`
              : stats.lowStockRolls > 0
              ? `${stats.lowStockRolls} roll${stats.lowStockRolls !== 1 ? "s" : ""} running low`
              : "Ankara, Lace, George & more"
          }
        />
        <StatCard
          label="Cash Collected"
          value={maskMoney(formatCurrency(stats.todayCash))}
          icon={Banknote}
          color="bg-green-100 text-green-600"
          sub={stats.todayTransfer > 0 ? `+${maskMoney(formatCurrency(stats.todayTransfer))} transfer` : "Cash only today"}
        />
      </div>

      {/* Low / out of stock alert */}
      {lowStock.length > 0 && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800">
              {stats.outOfStock > 0 ? `${stats.outOfStock} fabric line${stats.outOfStock !== 1 ? "s" : ""} finished` : "Rolls running low — restock soon"}
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              {lowStock.map((p: any) => p.name).join(" · ")}
            </p>
          </div>
          <Link href="/inventory">
            <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100 text-xs flex-shrink-0">Restock</Button>
          </Link>
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Sell by Yard",      href: "/sales/new",      icon: ShoppingCart, color: "bg-purple-50 border-purple-200 hover:bg-purple-100"   },
            { label: "Fabric Stock",      href: "/inventory",      icon: Layers,       color: "bg-indigo-50 border-indigo-200 hover:bg-indigo-100"   },
            { label: "Customers",         href: "/customers",      icon: Users,        color: "bg-blue-50 border-blue-200 hover:bg-blue-100"         },
            { label: "Log Expense",       href: "/expenses?add=1", icon: Receipt,      color: "bg-amber-50 border-amber-200 hover:bg-amber-100",     mgr: true },
          ].filter((a: any) => !a.mgr || isManager).map(a => (
            <Link key={a.label} href={a.href}>
              <div className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors text-slate-800 ${a.color}`}>
                <a.icon className="w-5 h-5 text-slate-700" />
                <span className="text-sm font-semibold">{a.label}</span>
                <ArrowRight className="w-3.5 h-3.5 ml-auto text-slate-500" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Fabric Stock — sorted low/out first */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Layers className="w-4 h-4 text-purple-500" />Fabric & Textile Stock
            </h3>
            <Link href="/inventory"><span className="text-xs text-primary hover:underline cursor-pointer">Manage →</span></Link>
          </div>
          {fabricStock.length === 0 ? (
            <div className="text-center py-6">
              <Layers className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No fabric lines added yet</p>
              <p className="text-xs text-muted-foreground mt-1">Add Ankara, Lace, George, Aso-Oke, Chiffon…</p>
              <Link href="/inventory?add=1">
                <Button size="sm" className="mt-3 bg-purple-600 hover:bg-purple-700">Add First Fabric</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              {fabricStock.map((p: any) => {
                const isOut = p.stock === 0;
                const isLow = !isOut && p.stock <= (p.reorderLevel ?? 5);
                return (
                  <div key={p.id} className="flex items-center gap-3 py-2.5 border-b border-border/50 last:border-0">
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isOut ? "bg-red-500" : isLow ? "bg-amber-500" : "bg-emerald-500"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium truncate">{p.name}</span>
                        {isOut && <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px] px-1.5 py-0 flex-shrink-0">Finished</Badge>}
                        {isLow && <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0 flex-shrink-0">Low</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground">{p.sku || "No SKU"}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-semibold">{formatCurrency(Number(p.price))}<span className="text-xs font-normal text-muted-foreground">/yd</span></div>
                      {!isOut && <div className={`text-xs font-medium ${isLow ? "text-amber-600" : "text-emerald-600"}`}>{p.stock} yds</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Today's Sales */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Banknote className="w-4 h-4 text-green-500" />Today's Sales
            </h3>
            <Link href="/sales"><span className="text-xs text-primary hover:underline cursor-pointer">All sales →</span></Link>
          </div>
          {todaySales.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingCart className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No fabric sold today yet</p>
              <Link href="/sales/new">
                <Button size="sm" className="mt-3 bg-purple-600 hover:bg-purple-700">Record First Sale</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              {todaySales.map((s: any) => {
                const isCash = (s.paymentMethod || "cash").toLowerCase() === "cash";
                return (
                  <div key={s.id} className="flex items-center justify-between py-2.5 border-b border-border/50 last:border-0">
                    <div>
                      <div className="text-sm font-medium">{s.customerName ?? "Walk-in Customer"}</div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Badge
                          variant="outline"
                          className={`text-[10px] px-1.5 py-0 ${isCash ? "text-green-700 border-green-300" : "text-blue-700 border-blue-300"}`}
                        >
                          {isCash ? "Cash" : s.paymentMethod || "Transfer"}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(s.createdAt).toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                    </div>
                    <div className="text-sm font-semibold">{maskMoney(formatCurrency(Number(s.totalAmount ?? s.total ?? 0)))}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {isOwner && (
        <div className="bg-muted/40 border border-border rounded-xl p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-purple-500" />Owner Analytics
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Profit & Loss",       href: "/accounting/profit-loss" },
              { label: "Daily Cash Flow",      href: "/accounting/cash-flow" },
              { label: "Expense Reports",      href: "/accounting/expense-reports" },
              { label: "Customer Reports",     href: "/people/customer-reports" },
            ].map(l => (
              <Link key={l.label} href={l.href}>
                <Button variant="outline" size="sm" className="w-full text-xs">{l.label}</Button>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
